import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { fetchShopProducts, fetchCartSummary, createCheckoutSession } from "../lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, clearCart } = useCart();
  const [products, setProducts] = useState({});
  const [summary, setSummary] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ free_shipping_threshold: 50 });

  useEffect(() => {
    fetchShopProducts()
      .then((d) => {
        const map = {};
        d.products.forEach((p) => (map[p.id] = p));
        // Bundles also addressable by id with image/name/price/category
        (d.bundles || []).forEach((b) => {
          map[b.id] = {
            id: b.id,
            name: b.name,
            category: `Set · ${b.included_count} brushes`,
            image: b.image,
            price: b.price,
            isBundle: true,
          };
        });
        setProducts(map);
        setMeta({ free_shipping_threshold: d.free_shipping_threshold });
      })
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    if (items.length === 0) {
      setSummary({ items: [], subtotal: 0, shipping: 0, total: 0, currency: "usd" });
      return;
    }
    fetchCartSummary(items).then(setSummary).catch(() => setSummary(null));
  }, [items]);

  const empty = items.length === 0;

  const handleCheckout = async () => {
    setError(null);
    setCheckingOut(true);
    try {
      const res = await createCheckoutSession({
        items,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      setError("Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  const amountToFreeShip = useMemo(() => {
    if (!summary) return null;
    return Math.max(0, meta.free_shipping_threshold - summary.subtotal);
  }, [summary, meta]);

  return (
    <div data-testid="cart-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <Link to="/shop" className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900">
        ← Continue Shopping
      </Link>
      <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight mt-4">
        Your Cart
      </h1>

      {empty ? (
        <div data-testid="cart-empty" className="mt-16 border-t border-[#E5E0D8] pt-12 text-center">
          <p className="text-stone-600">Your cart is empty.</p>
          <Link
            to="/shop"
            className="inline-block mt-6 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
          >
            Discover the Shop
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16 mt-10">
          <div className="lg:col-span-2">
            <div className="border-t border-[#E5E0D8]">
              {items.map((it) => {
                const p = products[it.id];
                if (loadingProducts || !p) {
                  return (
                    <div key={it.id} className="border-b border-[#E5E0D8] py-6 animate-pulse">
                      <div className="h-20 bg-[#F2EFEB]" />
                    </div>
                  );
                }
                return (
                  <div
                    key={it.id}
                    data-testid={`cart-row-${it.id}`}
                    className="border-b border-[#E5E0D8] py-6 flex gap-5 items-center"
                  >
                    {p.isBundle ? (
                      <div className="w-24 h-28 sm:w-28 sm:h-32 bg-[#F2EFEB] overflow-hidden shrink-0">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <Link to={`/brush/${p.id}`} className="w-24 h-28 sm:w-28 sm:h-32 bg-[#F2EFEB] overflow-hidden shrink-0">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                      </Link>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="overline mb-1">{p.category}</div>
                      {p.isBundle ? (
                        <div className="font-serif-display text-xl text-stone-900 leading-tight">{p.name}</div>
                      ) : (
                        <Link to={`/brush/${p.id}`} className="font-serif-display text-xl text-stone-900 leading-tight">
                          {p.name}
                        </Link>
                      )}
                      <div className="mt-3 flex items-center gap-3">
                        <div className="inline-flex border border-[#E5E0D8]">
                          <button
                            data-testid={`cart-qty-dec-${it.id}`}
                            onClick={() => updateQty(it.id, it.qty - 1)}
                            className="px-3 py-1.5 text-stone-700 hover:bg-[#F2EFEB]"
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="px-4 py-1.5 text-sm text-stone-900 min-w-[2.5rem] text-center" data-testid={`cart-qty-${it.id}`}>
                            {it.qty}
                          </span>
                          <button
                            data-testid={`cart-qty-inc-${it.id}`}
                            onClick={() => updateQty(it.id, it.qty + 1)}
                            className="px-3 py-1.5 text-stone-700 hover:bg-[#F2EFEB]"
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                        <button
                          data-testid={`cart-remove-${it.id}`}
                          onClick={() => removeItem(it.id)}
                          className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif-display text-xl text-stone-900">
                        ${(p.price * it.qty).toFixed(2)}
                      </div>
                      <div className="text-xs text-stone-500 mt-1">${p.price.toFixed(2)} ea</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              data-testid="cart-clear"
              onClick={clearCart}
              className="mt-6 text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900"
            >
              Clear cart
            </button>
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-[#F2EFEB] p-6 sticky top-24">
              <div className="overline mb-4">Order Summary</div>
              <div className="space-y-3 text-sm text-stone-700">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">
                    {summary ? `$${summary.subtotal.toFixed(2)}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span data-testid="cart-shipping">
                    {summary ? (summary.shipping === 0 ? "Free" : `$${summary.shipping.toFixed(2)}`) : "—"}
                  </span>
                </div>
                {amountToFreeShip > 0 && (
                  <p className="text-xs text-stone-500 leading-relaxed pt-1">
                    Add <span className="text-stone-900">${amountToFreeShip.toFixed(2)}</span> more for free shipping.
                  </p>
                )}
              </div>
              <div className="border-t border-[#E5E0D8] mt-5 pt-5 flex justify-between items-baseline">
                <span className="overline">Total</span>
                <span className="font-serif-display text-3xl text-stone-900" data-testid="cart-total">
                  {summary ? `$${summary.total.toFixed(2)}` : "—"}
                </span>
              </div>

              <button
                data-testid="cart-checkout-btn"
                onClick={handleCheckout}
                disabled={checkingOut || !summary || summary.total <= 0}
                className="w-full mt-6 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
              >
                {checkingOut ? "Redirecting…" : "Checkout Securely"}
              </button>

              {error && (
                <p data-testid="cart-error" className="mt-3 text-xs text-red-700">
                  {error}
                </p>
              )}

              <p className="text-xs text-stone-500 mt-5 leading-relaxed">
                Payments processed securely by Stripe. This shop is in test mode — use card 4242 4242 4242 4242 with any future expiry, any CVC, any ZIP.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
